/**
 * One person, the same way on every People page (Miles, 2026-10-23): the main People page and a
 * group's People page show the same thing; the main page just has more of it.
 *
 * Everything between you and a person comes from "sources", and a group and tutoring are the same
 * kind of thing in code. A group is filed under Purchases; a family's lessons and a tutor's pay
 * under Tutoring. Each source has:
 *   - rows that add up exactly to its balance (Their items, Paid you, ... / Earned, Paid out, ...)
 *   - a statement, oldest first, with a running balance that ends exactly on that balance
 *   - for a group, the items still unpaid there
 * The person's overview adds the sources up (overallTotal) and merges their statements, so the
 * history reads like a bank statement: "Oct 14 · Tutoring: 1 hour lesson −$20.00 · owes you $90.63".
 *
 * Signs: positive = adds to what they owe you; negative = takes off it. (Screens show a statement
 * from the person's side, like their bank account: a purchase is minus, a payment or tutoring pay
 * is plus.)
 */
import { shownCents } from "./shown.ts";
import { overallTotal, type BalanceCategory } from "./overall.ts";
import { groupBreakdown } from "./peopleView.ts";
import {
  studentSummary,
  tutorPayRows,
  tutorSummary,
  type Tutor,
  type TutoringEntry,
  type TutoringStudent,
} from "./tutoring.ts";
import type { Ledger, OverdueLevel, PersonStatus } from "./engine/ledger.ts";
import type { GroupState } from "./types.ts";

export type SourceRowKey =
  "items" | "fronted" | "paid" | "earned" | "paid-out" | "bought" | "charged" | "family-paid";

/** One number in a source's breakdown. `cents` is its effect: the rows add up to the balance. */
export interface SourceRow {
  key: SourceRowKey;
  cents: number;
}

export type EntryKind =
  | "item"
  | "fronted"
  | "payment"
  | "lesson"
  | "payout"
  | "bought"
  | "purchase"
  | "charge"
  | "family-payment";

/** One line of a person's statement. */
export interface StatementEntry {
  /** The item, payment or Tutoring record it comes from. */
  id: string;
  kind: EntryKind;
  /** "2026-10-14", or with a time ("2026-10-14T16:00") when it has one. */
  on: string;
  /** Item name, or what was bought. */
  name?: string;
  /** A payment's note ("Zelle"). */
  note?: string;
  /** Lessons and hours bought. */
  minutes?: number;
  /** Lessons: when (24-hour, "16:00" to "17:00"), and the student's full name. */
  start?: string;
  end?: string;
  student?: string;
  /** Payouts: how it was paid. */
  method?: string;
  /** Effect in whole cents: positive adds to what they owe you. */
  cents: number;
  /** This source's balance after it. */
  running: number;
  /** Which source it belongs to (PersonSource.key). */
  source: string;
}

/** One unpaid item in a group (oldest first). */
export interface UnpaidLine {
  itemId: string;
  name: string;
  date: string;
  remainingMicro: number;
  partial: boolean;
}

export interface PersonSource {
  /** Unique within the person: "group:<id>", "student:<id>", "tutor:<id>". */
  key: string;
  kind: "group" | "tutoring";
  role?: "student" | "tutor";
  category: BalanceCategory;
  /** "I can't fly", "Tutoring pay", "Tutoring for Ada Lin". */
  label: string;
  groupId?: string;
  personId?: string;
  /** Positive: they owe you here. */
  balanceCents: number;
  rows: SourceRow[];
  /** Oldest first. */
  entries: StatementEntry[];
  unpaid: UnpaidLine[];
  /** Groups: the ledger's own words for this person. */
  status?: PersonStatus;
  color?: string;
  totalMicro?: number;
  balanceMicro?: number;
  oldestUnpaid?: string | null;
  daysOverdue?: number;
  overdue?: OverdueLevel;
  lastPaid?: string | null;
  /** The person set as "you" in this group: their own share, not a balance with you. */
  you?: boolean;
}

/** Running balances by rounding the running total as it goes, so it ends exactly on the balance. */
function withRunning(
  raw: { e: Omit<StatementEntry, "cents" | "running">; micro: number }[],
): StatementEntry[] {
  let sum = 0;
  let shown = 0;
  return raw.map(({ e, micro }) => {
    sum += micro;
    const next = shownCents(sum);
    const cents = next - shown;
    shown = next;
    return { ...e, cents, running: next };
  });
}

const byDate = <T extends { e: { on: string } }>(a: T, b: T) =>
  a.e.on.slice(0, 10) < b.e.on.slice(0, 10)
    ? -1
    : a.e.on.slice(0, 10) > b.e.on.slice(0, 10)
      ? 1
      : 0;

/** One person in one group, from its ledger: rows, statement and unpaid items. */
export function groupSource(
  state: GroupState,
  ledger: Ledger,
  personId: string,
  label: string,
  groupId: string,
  key = `group:${groupId}`,
  /**
   * Whether this is your own share (main People decides: the group's "you", unless someone
   * else's account holds that person). Absent: the group's "you" setting.
   */
  isYou?: boolean,
): PersonSource | null {
  const p = ledger.byPersonId[personId];
  if (!p) return null;
  const payments = state.paymentOrder
    .map((id) => state.payments[id])
    .filter((x) => x && x.personId === personId) as NonNullable<GroupState["payments"][string]>[];
  const b = groupBreakdown(p);
  const you = isYou ?? state.settings.ownerPersonId === personId;
  const raw: { e: Omit<StatementEntry, "cents" | "running">; micro: number }[] = [];
  for (const c of ledger.items) {
    const share = c.shares[personId];
    if (share)
      raw.push({
        e: { id: c.item.id, kind: "item", on: c.item.date, name: c.item.name, source: key },
        micro: share,
      });
    if (c.item.paidBy === personId)
      raw.push({
        e: {
          id: `${c.item.id}:paid`,
          kind: "fronted",
          on: c.item.date,
          name: c.item.name,
          source: key,
        },
        micro: -c.totalMicro,
      });
  }
  for (const pay of payments)
    raw.push({
      e: {
        id: pay.id,
        kind: "payment",
        on: pay.at,
        note: pay.note,
        source: key,
      },
      micro: -pay.cents * 10_000,
    });
  // Oldest first; on the same day, what was bought before what paid for it (stable otherwise).
  raw.sort(byDate);
  return {
    key,
    kind: "group",
    category: "purchases",
    label,
    groupId,
    personId,
    balanceCents: you ? 0 : b.balanceCents || 0,
    rows: (you
      ? [{ key: "items" as const, cents: b.itemsCents }]
      : [
          { key: "items" as const, cents: b.itemsCents },
          { key: "fronted" as const, cents: -b.frontedCents },
          { key: "paid" as const, cents: -b.paidCents },
        ]
    ).map((r) => ({ ...r, cents: r.cents || 0 })),
    entries: you ? [] : withRunning(raw),
    unpaid: p.unpaid.map((u) => ({ ...u })),
    // The group's "you" held by someone else's account is them: say their own status.
    status:
      p.status === "you" && !you
        ? b.balanceCents > 0
          ? "owes"
          : b.balanceCents < 0
            ? "owed"
            : b.itemsCents
              ? "paid"
              : "none"
        : p.status,
    color: p.color,
    totalMicro: p.totalMicro,
    balanceMicro: p.balanceMicro,
    oldestUnpaid: p.oldestUnpaid,
    daysOverdue: p.daysOverdue,
    overdue: p.overdue,
    lastPaid: p.lastPaid,
    ...(you ? { you: true } : {}),
  };
}

/** Lesson times and students' names, to say each lesson in full. */
export interface LessonLookup {
  slot?: (slotId: string) => { startsAt: string; endsAt: string } | undefined;
  student?: (studentId: string) => string;
}

/** A tutor's pay as a source: earned is credited to them, money paid to them adds back. */
export function tutorSource(
  tutor: Tutor,
  entries: readonly TutoringEntry[],
  look: LessonLookup = {},
  label = "Tutoring pay",
  key = `tutor:${tutor.id}`,
): PersonSource {
  const sum = tutorSummary(tutor, entries);
  const pay = tutorPayRows(tutor, entries);
  let running = 0;
  const list: StatementEntry[] = pay.rows.map((r) => {
    // A row's cents are from their side (pay is plus); here plus means they owe you more.
    const cents = -r.cents || 0;
    running += cents;
    const slot = r.slotId ? look.slot?.(r.slotId) : undefined;
    const base = { id: r.id, on: r.on, cents, running, source: key };
    if (r.kind === "lesson")
      return {
        ...base,
        kind: "lesson" as const,
        on: slot ? slot.startsAt : r.on,
        minutes: r.minutes ?? 0,
        ...(slot ? { start: slot.startsAt.slice(11, 16), end: slot.endsAt.slice(11, 16) } : {}),
        ...(r.studentId && look.student ? { student: look.student(r.studentId) } : {}),
      };
    if (r.kind === "paid")
      return {
        ...base,
        kind: "payout" as const,
        method: r.method ?? "",
      };
    return { ...base, kind: "bought" as const, name: r.note ?? "" };
  });
  return {
    key,
    kind: "tutoring",
    role: "tutor",
    category: "tutoring",
    label,
    balanceCents: -sum.owedCents || 0,
    rows: [
      { key: "earned" as const, cents: -sum.earnedCents || 0 },
      { key: "paid-out" as const, cents: sum.paidOutCents },
      { key: "bought" as const, cents: sum.boughtCents },
    ],
    entries: list,
    unpaid: [],
  };
}

/** A family's lessons as a source: what they're charged, less what they paid. */
export function studentSource(
  student: TutoringStudent,
  entries: readonly TutoringEntry[],
  label: string,
  look: LessonLookup = {},
  key = `student:${student.id}`,
): PersonSource {
  const sum = studentSummary(student.id, entries);
  let running = 0;
  const list: StatementEntry[] = [];
  for (const e of entries
    .filter((x) => x.studentId === student.id)
    .sort((a, b) => (a.on < b.on ? -1 : a.on > b.on ? 1 : 0))) {
    const cents = (e.kind === "payment" ? -e.cents : e.cents) || 0;
    if (!["purchase", "session", "item", "adjust", "payment"].includes(e.kind)) continue;
    running += cents;
    const slot = e.slotId ? look.slot?.(e.slotId) : undefined;
    const base = { id: e.id, on: e.on, cents, running, source: key };
    if (e.kind === "session")
      list.push({
        ...base,
        kind: "lesson",
        on: slot ? slot.startsAt : e.on,
        minutes: e.minutes,
        ...(slot ? { start: slot.startsAt.slice(11, 16), end: slot.endsAt.slice(11, 16) } : {}),
      });
    else if (e.kind === "purchase")
      list.push({ ...base, kind: "purchase", minutes: e.minutes, note: e.note });
    else if (e.kind === "payment")
      list.push({ ...base, kind: "family-payment", method: e.method, note: e.note });
    else list.push({ ...base, kind: "charge", name: e.note, minutes: e.minutes });
  }
  return {
    key,
    kind: "tutoring",
    role: "student",
    category: "tutoring",
    label,
    balanceCents: sum.balanceCents || 0,
    rows: [
      { key: "charged" as const, cents: sum.chargedCents },
      { key: "family-paid" as const, cents: -sum.paidCents || 0 },
    ],
    entries: list,
    unpaid: [],
  };
}

export interface PersonOverview {
  sources: PersonSource[];
  /** Total spent: their items in every group (what they spent), the sum of the items rows. */
  spentCents: number;
  /** Every group added up, every tutoring source added up, and both: exact sums of the sources. */
  purchasesCents: number;
  tutoringCents: number;
  totalCents: number;
  /** Every source's statement in one, newest first, with the overall running balance. */
  statement: (StatementEntry & { overall: number })[];
}

/** One person across their sources (or one source, inside a group). */
export function personOverview(sources: readonly PersonSource[]): PersonOverview {
  const t = overallTotal(
    sources.map((s) => ({
      category: s.category,
      label: s.label,
      balanceMicro: s.balanceCents * 10_000,
    })),
  );
  // Merge oldest first by day (source order breaks ties), keeping each source's running balance;
  // the overall running balance is the sum of every source's latest running balance.
  const tagged = sources.flatMap((s, si) => s.entries.map((e, i) => ({ e, si, i })));
  tagged.sort((a, b) => {
    const d = a.e.on.slice(0, 10).localeCompare(b.e.on.slice(0, 10));
    return d || a.si - b.si || a.i - b.i;
  });
  const latest = new Map<string, number>();
  const merged = tagged.map(({ e }) => {
    latest.set(e.source, e.running);
    let overall = 0;
    for (const v of latest.values()) overall += v;
    return { ...e, overall };
  });
  const spentCents = sources
    .filter((s) => s.category === "purchases")
    .reduce((n, s) => n + (s.rows.find((r) => r.key === "items")?.cents ?? 0), 0);
  return {
    sources: [...sources],
    spentCents,
    purchasesCents: t.purchasesCents,
    tutoringCents: t.tutoringCents,
    totalCents: t.totalCents,
    statement: merged.reverse(),
  };
}

/** How much of what they spent is covered (payments and tutoring), 0 to 1; null with no purchases. */
export function overviewProgress(o: PersonOverview): number | null {
  if (o.spentCents <= 0 || o.sources.every((s) => s.you)) return null;
  return Math.max(0, Math.min(1, (o.spentCents - Math.max(0, o.totalCents)) / o.spentCents));
}

/** The swatch's ring on main People, the way a group shows paid, part paid or unpaid. */
export function overviewStatus(o: PersonOverview): "paid" | "off" | "partial" | "unpaid" {
  if (o.totalCents <= 0) return "paid";
  return o.totalCents < o.spentCents ? "partial" : "unpaid";
}

/**
 * Their overall balance at the end of each of the last `weeks` weeks, from their statement (for
 * the little line on main People), in micro like a group's own line. Null when there's nothing
 * to draw.
 */
export function overviewTrend(o: PersonOverview, today: string, weeks = 16): number[] | null {
  if (!o.statement.length) return null;
  const oldestFirst = [...o.statement].reverse();
  const end = new Date(`${today}T12:00:00Z`);
  const points: number[] = [];
  for (let w = weeks - 1; w >= 0; w--) {
    const d = new Date(end);
    d.setUTCDate(d.getUTCDate() - 7 * w);
    const day = d.toISOString().slice(0, 10);
    let v = 0;
    for (const e of oldestFirst) if (e.on.slice(0, 10) <= day) v = e.overall;
    points.push(v * 10_000);
  }
  return points.some((p) => p !== 0) ? points : null;
}
